import { Subscription } from 'rxjs';
import { Component, OnInit, TemplateRef, ViewChild, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { PaginationService } from 'libs/portal/src/services/pagination/pagination.service';
import { LayoutService, ScreenSize } from 'libs/portal/src/layout/layout.service';
import { FilterService } from 'libs/portal/src/services/filter/filter.service';
import { GenericService } from 'libs/portal/src/services/generic/generic.service';
import { MemoryService } from 'libs/portal/src/services/memory/memory.service';
import { NavigationService } from 'libs/portal/src/services/navigation/navigation.service';
import { TitleService } from 'libs/portal/src/services/title/title.service';
import { ToolsService } from 'libs/portal/src/services/tools/tools.service';
import { UserRightsService } from 'libs/portal/src/services/user-rights/user-rights.service';
import { CardTableAction, CardTableOptions } from 'libs/portal/src/modules/tools/card-table/models';
import { CardTableHeader } from 'libs/portal/src/modules/tools/card-table/models/card-table-header.model';
import { Endpoints } from '@huge-monorepo/portal/config';
import { CardTableComponent } from 'libs/portal/src/modules/tools/card-table/components';
import { MikrotikBooleans } from 'libs/portal/src/models/MikrotikBooleans';
import { Routes } from '@huge-monorepo/portal/config';
import { GenericTableHeaderOptions, RefreshData } from 'libs/portal/src/modules/tools/card-table/models/generic-table-header-options.model';

export interface Device {
  id: number;
  name: string;
  data: string;
  status: string;
  selected: boolean;
  color: string;
}

@Component({
  selector: 'app-mikrotik-devices',
  templateUrl: './mikrotik-devices.page.html',
  styleUrls: ['./mikrotik-devices.page.scss'],
})
export class MikrotikDevicesPageComponent implements OnInit, OnDestroy {
  @ViewChild('sticky') sticky: TemplateRef<any>;
  @ViewChild('CDRTable', { static: false }) public CDRTable: CardTableComponent;

  data: Device[] = [];
  ScreenSize = ScreenSize;
  groupID: number;

  totalCdrDevices: number;
  loadingCDRDevices = false;
  cdrLoadingError = false;
  orderCDRBy = 'DeviceNum';
  cdrTableCount = 10;
  cdrSearch = '';
  cdrTableOffset = 0;
  currentCDRPage = 1;
  cdrDesc = false;
  obfuscateData = false;
  authSubscription: Subscription;

  mikrotikBools: MikrotikBooleans = {
    showEvents: false,
    regex: false,
    showAllocationData: true,
    showStatusChange: false,
    showCluster: false,
    showGroups: false,
    showScheduler: true,
    onlyACS: true,
    listcount: true,
    lastsnapshot: true,
    lastping: true,
  };

  firstLoad = true;

  getCdrDevicesRequestBody = {
    Online: null,
    BundleStart: null,
    BundleEnd: null,
    GroupID: null,
    ACS: true,
  };

  constructor(
    private titleService: TitleService,
    private filterService: FilterService,
    private navigationService: NavigationService,
    public layoutService: LayoutService,
    private genericService: GenericService,
    private paginationService: PaginationService,
    private router: Router,
    private endpoints: Endpoints,
    private tools: ToolsService,
    private auth: UserRightsService,
    private memory: MemoryService,
    private routeDictionary: Routes,
  ) {}

  headerOptions: GenericTableHeaderOptions = {
    ShowSearch: true,
    SearchPlaceholder: 'Search for devices',
    DefaultSort: 'DeviceNum',
    DefaultDesc: false,
    SortItems: [
      {
        Name: 'DeviceNum',
        Display: 'Name',
      },
      {
        Name: 'DeviceType',
        Display: 'Type',
      },
      {
        Name: 'LastIP',
        Display: 'Public IP',
      },
    ],
  };

  cdrTableOptions: CardTableOptions = {
    titleProperty: 'name',
    subTitleProperty: '',
    additionalProperty: '',
    rowId: 'id',
    indicatorColorProperty: 'color',
    selectedProperty: 'selected',
    isSelectable: false,
    flexCardLayout: false,
    propertyRowExclusions: ['id', 'selected', 'color'],
    stylePropertyPrefix: 'mikrotik',
    clickableFlexCard: false,
    textSize: 'regular',
  };

  keepAliveTableHeaders: CardTableHeader[] = [
    {
      ID: 'name',
      Text: 'Name',
      Width: 20,
    },
    {
      ID: 'deviceType',
      Text: 'Type',
      Width: 10,
    },
    {
      ID: 'productClass',
      Text: 'Class',
      Width: 10,
    },
    {
      ID: 'customer',
      Text: 'Customer',
      Width: 29,
    },
    {
      ID: 'publicIP',
      Text: 'Public IP',
      Width: 10,
    },
    {
      ID: 'Network',
      Text: 'Network',
      Width: 18,
    },
  ];

  cdrRowActions: CardTableAction[] = [
    {
      title: 'View',
      iconSrc: 'assets/icon/eye-outline.svg',
      color: 'primary',
      showIcon: true,
    },
  ];

  setCDRTableToRefreshing() {
    setTimeout(() => {
      if (this.CDRTable) {
        this.CDRTable.StartRefresh();
      }
    });
  }

  setCDRTableToFinished() {
    setTimeout(() => {
      if (this.CDRTable) {
        this.CDRTable.EndRefresh();
      }
    });
  }

  refreshTable(data: RefreshData): void {
    this.cdrSearch = data.Search ?? '';
    this.orderCDRBy = data.Sort ?? '';
    this.cdrDesc = data.Desc ?? this.cdrDesc;
    this.cdrTableOffset = 0;
    this.currentCDRPage = 1;
    this.getDevices(this.orderCDRBy, this.cdrSearch, this.cdrTableCount, this.cdrTableOffset, this.cdrDesc);
  }

  getDevices(orderBy: string, search: string, count: number, offset: number, desc: boolean) {
    this.loadingCDRDevices = true;
    this.setCDRTableToRefreshing();
    this.cdrLoadingError = false;
    this.genericService.GET(this.endpoints.GET_MIKROTIK_DEVICES(this.mikrotikBools, this.groupID, search, count, offset, orderBy, desc)).subscribe(
      (resp: Object) => {
        const deviceData = resp['body'];
        this.totalCdrDevices = this.tools.ExtractPaginationFromHeaders(resp['headers']);
        if (deviceData?.length) {
          const tmpData = [];
          deviceData.forEach((element) => {
            const tempObj = {
              id: element.ID,
              name: `${element.DeviceNum}`,
              deviceType: element.DeviceType == null ? '-' : element.DeviceType,
              productClass: element.ProductClass == null ? '-' : element.ProductClass.Name,
              customer:
                element.Customer?.Reference != '' ? (this.obfuscateData ? this.tools.Obfuscate(element.Customer?.Reference, 'Customer', 8) : element.Customer?.Reference) : '-',
              publicIP: element.LastIP != null ? element.LastIP : '-',
              selected: false,
              color: element.MikrotikOnline ? '#3FC944' : '#C96D01',
            };

            if (element.LastSnapshotEvent && element.LastSnapshotEvent.CurrentOperator) {
              tempObj['Network'] = element.LastSnapshotEvent.CurrentOperator != '' ? element.LastSnapshotEvent.CurrentOperator : '';
            }

            tmpData.push(tempObj);
          });
          this.data = tmpData;
        } else {
          this.data = [];
        }

        this.loadingCDRDevices = false;
        this.setCDRTableToFinished();
        this.firstLoad = false;
        this.paginationService.emit();
      },
      () => {
        this.loadingCDRDevices = false;
        this.paginationService.emit();
        this.cdrLoadingError = true;
        this.setCDRTableToFinished();
      },
    );
  }

  getPagedDevices(pageIndex: number) {
    this.cdrTableOffset = (pageIndex - 1) * this.cdrTableCount;
    this.currentCDRPage = pageIndex;
    this.getDevices(this.orderCDRBy, this.cdrSearch, this.cdrTableCount, this.cdrTableOffset, this.cdrDesc);
  }

  ngOnInit() {
    this.authSubscription = this.auth.GetUser().subscribe((user) => {
      this.obfuscateData = user.isObfuscated;
      if (!this.firstLoad) {
        this.getDevices(this.orderCDRBy, this.cdrSearch, this.cdrTableCount, this.cdrTableOffset, this.cdrDesc);
      }
    });
    this.titleService.pageTitle = 'Mikrotik Devices';
    this.groupID = this.auth.GetGroupID();
    this.getCdrDevicesRequestBody.GroupID = this.groupID;

    const memory = this.memory.getDetails();
    if (memory != null && memory.isCdr) {
      this.getDevices(memory.orderBy, memory.search, memory.tableCount, memory.tableOffset, memory.desc);
      this.cdrSearch = memory.search;
    } else {
      this.getDevices(this.orderCDRBy, '', this.cdrTableCount, this.cdrTableOffset, this.cdrDesc);
      this.cdrSearch = '';
    }
  }

  ngOnDestroy(): void {
    this.authSubscription.unsubscribe();
  }

  ngAfterViewInit(): void {
    this.layoutService.addStickyHeaderForRoute(this.navigationService.GetCurrentRoute(), this.sticky);
  }

  onCdrAction(event) {
    if (event.title == 'View' && event.row != null) {
      this.memory.setDetails(true, this.orderCDRBy, this.cdrSearch, this.cdrTableCount, this.cdrTableOffset, this.cdrDesc);
      this.router.navigate([this.routeDictionary.MikrotikDeviceDetails], {
        state: {
          data: {
            CdrID: event.row.id,
            KaID: event.row.kadevice_id,
          },
        },
      });
    }
  }

  refreshData(event) {
    setTimeout(() => {
      event.target.complete();
    }, 2000);
  }
}
